/**
 * useWorkspaceForm Hook - Shared Workspace Form Validation
 *
 * Extracts common form state and validation logic used by both
 * WorkspaceCreationForm and WorkspaceModal components.
 *
 * @module hooks/useWorkspaceForm
 */

import type { ChangeEvent } from 'react';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WORKSPACE_NAME_MIN_LENGTH,
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_DESCRIPTION_MAX_LENGTH,
} from '@/constants/workspace';

interface FormErrors {
  name?: string;
  description?: string;
}

interface UseWorkspaceFormOptions {
  initialName?: string;
  initialDescription?: string;
}

interface UseWorkspaceFormReturn {
  name: string;
  description: string;
  errors: FormErrors;
  nameTouched: boolean;
  setName: (value: string) => void;
  setDescription: (value: string) => void;
  setErrors: (errors: FormErrors) => void;
  setNameTouched: (value: boolean) => void;
  validateForm: () => boolean;
  handleNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleNameBlur: () => void;
  handleDescriptionChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  resetForm: () => void;
}

export function useWorkspaceForm(
  options: UseWorkspaceFormOptions = {}
): UseWorkspaceFormReturn {
  const { t } = useTranslation();

  const [name, setName] = useState(options.initialName ?? '');
  const [description, setDescription] = useState(
    options.initialDescription ?? ''
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [nameTouched, setNameTouched] = useState(false);

  const validateName = useCallback(
    (value: string): string | undefined => {
      if (!value.trim()) {
        return t('workspace.validation.nameRequired');
      }
      if (value.trim().length < WORKSPACE_NAME_MIN_LENGTH) {
        return t('workspace.validation.nameTooShort', {
          min: WORKSPACE_NAME_MIN_LENGTH,
        });
      }
      if (value.length > WORKSPACE_NAME_MAX_LENGTH) {
        return t('workspace.validation.nameTooLong', {
          max: WORKSPACE_NAME_MAX_LENGTH,
        });
      }
      return undefined;
    },
    [t]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const nameError = validateName(name);
    if (nameError) {
      newErrors.name = nameError;
    }

    if (description.length > WORKSPACE_DESCRIPTION_MAX_LENGTH) {
      newErrors.description = t('workspace.validation.descriptionTooLong', {
        max: WORKSPACE_DESCRIPTION_MAX_LENGTH,
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, description, t, validateName]);

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setName(value);
      setNameTouched(true);
      const nameError = validateName(value);
      setErrors((prev) => ({ ...prev, name: nameError }));
    },
    [validateName]
  );

  const handleNameBlur = useCallback(() => {
    setNameTouched(true);
    const nameError = validateName(name);
    setErrors((prev) => ({ ...prev, name: nameError }));
  }, [name, validateName]);

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(event.target.value);
      if (errors.description) {
        setErrors((prev) => ({ ...prev, description: undefined }));
      }
    },
    [errors.description]
  );

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setErrors({});
    setNameTouched(false);
  }, []);

  return {
    name,
    description,
    errors,
    nameTouched,
    setName,
    setDescription,
    setErrors,
    setNameTouched,
    validateForm,
    handleNameChange,
    handleNameBlur,
    handleDescriptionChange,
    resetForm,
  };
}
